import { Injectable } from '@angular/core';
import {Observable} from "rxjs";
import {catchError, tap} from "rxjs/operators";
import { HttpClient, HttpParams } from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {HelperService} from "../helper.service";
import {BaseService} from "./base.service";
import { SoilSampleModel } from '../models/soil-sample-model';
import { SoftTissueSampleModel } from '../models/soft-tissue-sample-model';
import { AllSamplesGroupModel } from '../models/all-samples-group-model';
import { PlantPart } from '../models/plant-part';


@Injectable({
  providedIn: 'root'
})
export class SampleService extends BaseService {

  private API_PIN_MANAGEMENT_URL = this.API_BASE_URL + "pin-management"; 

  constructor(
      private httpClient: HttpClient,
      public snackBar: MatSnackBar,
      public helper: HelperService
  ) {
    super();
  }

  /// GET ///
    getSoilSamplesListByPinId(id: number): Observable<SoilSampleModel[]> {
      return this.httpClient.get<SoilSampleModel[]>(this.API_PIN_MANAGEMENT_URL + `/pins/${id}/samples/soil`)
          .pipe(
              catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Retrieving Soil Sample List','close', {
                  duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
              })
          );
    }

    getSoftTissueSamplesListByPinId(id: number): Observable<SoftTissueSampleModel[]> {
      return this.httpClient.get<SoftTissueSampleModel[]>(this.API_PIN_MANAGEMENT_URL + `/pins/${id}/samples/softtissue`)
          .pipe(
              catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Retrieving Plant Part List','close', {
                  duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
              })
          );
    }

    getSoftTissuePlantPartList(): Observable<PlantPart[]> {
      return this.httpClient.get<PlantPart[]>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/plantparts`)
          .pipe(
              catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Retrieving Soft Tissue Sample List','close', {
                  duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
              })
          );
    }


    getPaginatedSampleListByGroupId(id: number, page: number, size: number,searchQuery:string, fromDate:string, toDate: string, pinTypeId?: any): Observable<AllSamplesGroupModel> {

      let params = new HttpParams()
          .set('page', page.toString())
          .append('size', size.toString())
          
  
      if (searchQuery) {
          params = params.append('searchQuery', searchQuery);
      }
  
      if (fromDate) {
          params = params.append('fromDate', fromDate);
      }
  
      if (toDate) {
          params = params.append('toDate', toDate);
      }
  
      if (pinTypeId) {
        params = params.append('pinTypeId', pinTypeId.toString());
    }
    
      return this.httpClient.get<AllSamplesGroupModel>(this.API_PIN_MANAGEMENT_URL + `/samplegroups/${id}`, {params})
          .pipe(
              catchError((err) => {
                  this.hasError.next(true);
                  this.snackBar.open('Error retrieving samples for this group','close', {
                      duration: 5000,
                  });
                  this.hasError.next(false);
                  return this.errorHandler(err);
              })
          );
  }




  //POST
  postSoilSample(pinId: number, pinSampleGroupId:number, name: string, sampleDepth?: string, rockPercentage?: string, note?:string, seasonFieldDetailId?: number): Observable<any> {
    const body: any = {
        pinId: pinId, 
        pinSampleGroupId: pinSampleGroupId,
        name: name,
        sampleDepth: sampleDepth,
        rockPercentage: rockPercentage,
        note: note
    };
    if (seasonFieldDetailId) body.seasonFieldDetailId = seasonFieldDetailId;
    

    return this.httpClient.post<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/soil`,body)
        .pipe(
            catchError((err) => {
              this.hasError.next(true);
              this.snackBar.open('Error Creating New Sample','close', {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
        );
  }

  postSoftTissueSample(pinId: number, pinSampleGroupId:number, name: string, plantPartId?: number, pinCropGrowthStageLinkId?: number, note?:string, seasonFieldDetailId?: number): Observable<any> {
    const body: any = {
        pinId: pinId, 
        pinSampleGroupId: pinSampleGroupId,
        name: name,
        plantPartId: plantPartId,
        pinCropGrowthStageLinkId: pinCropGrowthStageLinkId,
        note: note
    };
    if (seasonFieldDetailId) body.seasonFieldDetailId = seasonFieldDetailId;
    

    return this.httpClient.post<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/softtissue`,body)
        .pipe(
            catchError((err) => {
              this.hasError.next(true);
              this.snackBar.open('Error Creating New Sample','close', {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            })
        );
  }

  //PUT
  updateSoilSample(pinId: number, sampleId: number,pinSampleGroupId:number, name: string, sampleDepth?: string, rockPercentage?: string, note?:string, is_completed?: boolean): Observable<any> {

  const body = {
    pinId: pinId, 
    pinSampleGroupId: pinSampleGroupId,
    name: name,
    sampleDepth: sampleDepth,
    rockPercentage: rockPercentage,
    note: note,
    isCompleted: is_completed
}

    return this.httpClient.put<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/${sampleId}/soil` , body)
        .pipe(
            catchError((err) => {
              this.hasError.next(true);
              this.snackBar.open('Error Updating Sample','close', {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            }),
            tap(() => {
              this.snackBar.open('Update Successful', 'close', {
                duration: 5000,
              });
            })
        );
  }

  updateSoftTissueSample(pinId: number, sampleId: number, pinSampleGroupId:number, name: string, plantPartId?: number, pinCropGrowthStageLinkId?: number, note?:string, isCompleted?: boolean): Observable<any> {
    const body = {
      pinId: pinId, 
      pinSampleGroupId: pinSampleGroupId,
      name: name,
      plantPartId: plantPartId,
      pinCropGrowthStageLinkId: pinCropGrowthStageLinkId,
      note: note,
      isCompleted: isCompleted
  }
    return this.httpClient.put<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/${sampleId}/softtissue` , body)
        .pipe(
            catchError((err) => {
              this.hasError.next(true);
              this.snackBar.open('Error Updating Sample','close', {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            }),
            tap(() => {
              this.snackBar.open('Update Successful', 'close', {
                duration: 5000,
              });
            })
        );
  }


  moveSampleToGroup(samplepId: number, moveToGroupId: number): Observable<any> {
    
    const body = {}
    return this.httpClient.put<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/${samplepId}/move?moveToId=${moveToGroupId}` , body)
        .pipe(
            catchError((err) => {
              this.hasError.next(true);
              this.snackBar.open('Error Moving Sample Groups','close', {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            }),
            tap(() => {
              this.snackBar.open('Move Successful', 'close', {
                duration: 5000,
              });
            })
        );
  }


  batchCreateSamples(tenantId: number, pinSampleGroupId: number, sampleType: string, samples: any[]): Observable<any> {
    const body = { tenantId, pinSampleGroupId, sampleType, samples };
    return this.httpClient.post<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/batch`, body)
      .pipe(
        catchError((err) => {
          this.hasError.next(true);
          this.snackBar.open('Error Creating Batch Samples', 'close', { duration: 5000 });
          this.hasError.next(false);
          return this.errorHandler(err);
        })
      );
  }

  // DELETE ///
  deleteSampleById(id: number): Observable<any> {
    
    return this.httpClient.delete(this.API_PIN_MANAGEMENT_URL + `/pins/samples/${id}`,{ responseType: 'text' })
        .pipe(
            catchError((err) => {
              this.hasError.next(true);
              this.snackBar.open('Error Removing Sample','close', {
                duration: 5000,
              });
              this.hasError.next(false);
              return this.errorHandler(err);
            }),
            tap(() => {
              this.snackBar.open('Sample Removed Successfully', 'close', {
                duration: 5000,
              });
            })
        );
  }

  updateSampleLink(sampleId: number, seasonFieldDetailId: number): Observable<any> {
    return this.httpClient.put<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/${sampleId}/link`, { seasonFieldDetailId });
  }

  getDefinitionsForPin(pinId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/definitions/${pinId}`);
  }

  getSampleLink(sampleId: number): Observable<any> {
    return this.httpClient.get<any>(this.API_PIN_MANAGEMENT_URL + `/pins/samples/${sampleId}/link`);
  }
}
